
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingState } from "@/components/error-handling/LoadingState";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import LinkJobForm from "./LinkJobForm";
import { Job } from "@/types/job"; // Using global Job type
import JobsList from "./JobsList"; // Import the new component

// Mock data for jobs, ensuring posted field is always present
const mockJobs: Job[] = [ // Use the global Job type for mock data
  {
    id: "1",
    title: "Senior Software Engineer",
    status: "Open",
    candidates: 12,
    posted: "2023-05-15", 
    company: "Tech Solutions Inc.", 
    type: "Full-time",
    salary: "$120,000 - $150,000",
    description: "Description for Senior Software Engineer",
    location: "Remote"
  },
  {
    id: "2",
    title: "Product Manager",
    status: "Filled",
    candidates: 8,
    posted: "2023-04-20", 
    company: "Innovate Ltd.",
    type: "Full-time",
    salary: "$100,000 - $130,000",
    description: "Description for Product Manager",
    location: "New York"
  },
  {
    id: "3",
    title: "UX Designer",
    status: "On Hold",
    candidates: 5,
    posted: "2023-06-01", 
    company: "Creative Designs",
    type: "Contract",
    salary: "$70 - $90/hour",
    description: "Description for UX Designer",
    location: "San Francisco"
  },
];

interface ClientJobsTabProps { 
  clientId?: string;
}

const ClientJobsTab: React.FC<ClientJobsTabProps> = ({ clientId: propClientId }) => {
  const { clientId: paramClientId } = useParams();
  const clientId = propClientId || paramClientId; 

  const [isLoading, setIsLoading] = useState(false); 
  const [activeTab, setActiveTab] = useState("active");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (isLoading) {
    return <LoadingState message="Loading client jobs..." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Jobs</h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" onClick={() => setIsDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Link Job
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Link Job to Client</DialogTitle>
            </DialogHeader>
            <LinkJobForm
              clientId={clientId || ""}
              onSubmit={(jobId) => { console.log('Submitting', jobId); setIsDialogOpen(false); }}
              onCancel={() => setIsDialogOpen(false)}
              isSubmitting={false}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Tabs
        defaultValue="active"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="filled">Filled</TabsTrigger>
          <TabsTrigger value="all">All Jobs</TabsTrigger>
        </TabsList>
        <TabsContent value="active" className="mt-4">
          <JobsList jobs={mockJobs.filter(job => job.status === "Open")} />
        </TabsContent>
        <TabsContent value="filled" className="mt-4">
          <JobsList jobs={mockJobs.filter(job => job.status === "Filled")} />
        </TabsContent>
        <TabsContent value="all" className="mt-4">
          <JobsList jobs={mockJobs} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientJobsTab;
